import React, { useCallback, useRef, useState } from "react";
import SwipeableViews from "react-swipeable-views";
import Pagination from "../Pagination";
import YouTubeVideo from '../event/youtube_video'
import { totalSlidesCount as countSlides, nextIndexOnTap, isTap } from './main_slider_logic';

const styles = {
  root: {
    padding: '0 60px',
  },
  slideContainer: {
    padding: '0 5px',
  },
  slide: {
    padding: 5,
    minHeight: 100,
    borderRadius: '10px',
  },
  slideContent1: {
    borderRadius: '10px',
  },
  slideContent2: {
    borderRadius: '10px',
    border: 'none',
    height: '100%',
    width: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
  },
  slideNavButton: {
    position: 'absolute', top: '50%', transform: 'translateY(-50%)',
    width: 40, height: 40, borderRadius: '50%', border: 'none',
    background: 'rgba(255,255,255,0.8)', boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', zIndex: 2,
  },
  slideNavButtonLeft:  { left: 8 },
  slideNavButtonRight: { right: 8 },
};

const mainSlider = ({ photoUrl, videoIds, subPhotoUrls, circleMovieUrls = [] }) => {
  const [photoIndex, setPhotoIndex] = useState(0);
  const pressStartXRef = useRef(null);

  const handleChangeIndex = useCallback(
    (i) => {
      setPhotoIndex(i);
    },
    [setPhotoIndex]
  );

  const totalSlidesCount = countSlides({ videoIds, subPhotoUrls, circleMovieUrls });
  const goPreviousSlide = () => setPhotoIndex((i) => Math.max(0, i - 1));
  const goNextSlide = () => setPhotoIndex((i) => Math.min(totalSlidesCount - 1, i + 1));

  // @context TGW-2581 画像・動画スライドはタップで次へ（最後は先頭へ）。スワイプ終わりの click は無視する。
  //   YouTube スライドは iframe がタップを受けるため対象外。
  const handleSlidePress = (e) => {
    pressStartXRef.current = e.clientX;
  };
  const handleSlideTap = (e) => {
    if (!isTap(pressStartXRef.current, e.clientX)) return;
    setPhotoIndex((i) => nextIndexOnTap(i, totalSlidesCount));
  };
  const tapProps = { onPointerDown: handleSlidePress, onClick: handleSlideTap };

  // @context TGW-2581 React は muted を DOM プロパティにしか反映せず HTML 属性を出さない。
  //   iOS Safari 等は muted 属性の無い動画の自動再生を止めるため、属性も明示的に付ける。
  const ensureMutedAttribute = (video) => {
    if (!video) return;
    video.muted = true;
    video.setAttribute('muted', '');
  };

  const slides = [
    <figure key="main" className="adjust-box adjust-box-4x3 mb-0" style={styles.slide} {...tapProps}>
      <img
        data-src={photoUrl}
        alt="EventImage"
        className="adjust-box-inner lazyload w-100 object-fit-contain bg-graydark3"
        style={styles.slideContent1}
      />
    </figure>,
    ...(videoIds).map((id, idx) => (
      <figure key={`video-${idx}`} className="adjust-box adjust-box-4x3 mb-0" style={styles.slide}>
        <YouTubeVideo id={id} uniqId={idx} style={styles.slideContent2}/>
      </figure>
    )),
    ...(subPhotoUrls).map((url, idx) => (
      <figure key={`sub-${idx}`} className="adjust-box adjust-box-4x3 mb-0" style={styles.slide} {...tapProps}>
        <img
          data-src={url}
          alt="EventSubImage"
          className="adjust-box-inner lazyload w-100 object-fit-contain bg-graydark3"
          style={styles.slideContent1}
        />
      </figure>
    )),
    ...(circleMovieUrls).map((url, idx) => (
      <figure key={`circle-movie-${idx}`} className="adjust-box adjust-box-4x3 mb-0" style={styles.slide} {...tapProps}>
        <video
          ref={ensureMutedAttribute}
          src={url}
          muted
          autoPlay
          loop
          playsInline
          preload="metadata"
          className="adjust-box-inner w-100 object-fit-contain bg-graydark3"
          style={styles.slideContent1}
        />
      </figure>
    ))
  ];

  return (
    <React.Fragment>
      <div style={{ position: 'relative' }}>
        {slides.length > 1 && photoIndex > 0 && (
          <button type="button" aria-label="前のスライド"
            style={{ ...styles.slideNavButton, ...styles.slideNavButtonLeft }} onClick={goPreviousSlide}>
            <i className="fas fa-chevron-left" />
          </button>
        )}
        <SwipeableViews
          enableMouseEvents
          index={photoIndex}
          onChangeIndex={(i) => handleChangeIndex(i)}
          style={styles.root}
          slideStyle={styles.slideContainer}
        >
          {slides}
        </SwipeableViews>
        {slides.length > 1 && photoIndex < slides.length - 1 && (
          <button type="button" aria-label="次のスライド"
            style={{ ...styles.slideNavButton, ...styles.slideNavButtonRight }} onClick={goNextSlide}>
            <i className="fas fa-chevron-right" />
          </button>
        )}
        <Pagination
          dots={slides.length}
          index={photoIndex}
          onChangeIndex={(i) => handleChangeIndex(i)}
        />
      </div>
    </React.Fragment>
  );
};

export default mainSlider;